/**
 * useStatusFactorsState.ts
 *
 * Custom hook for managing status factors with local state, dirty tracking,
 * and dual-save logic (Apply per-factor, Save all globally).
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAnalytics } from '@center-technologies/analytics';
import type { Sphere, SphereStatusPoint } from 'types/global';
import type { FactorMode, PopulationSource, StatusValue } from 'types/spherePoints';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../utils/contextFlowSerialization';
import type { ApiClients } from '../context/ApiClientsContext';
import type {
  LocalFactor,
  FactorFormState,
  FactorValidationResult,
  FactorStatus,
} from '../types/statusFactors';
import { DEFAULT_FACTOR_FORM_STATE } from '../types/statusFactors';
export { countUnsavedChanges } from './countUnsavedChanges';
import { normaliseEmptyBehavior } from '@center-platform/spheres/rollup';
import { fromStored, toStored } from '../components/molecules/RelatedSpheresEditor/combineModel';
import { compileSet, decompileSet } from '../components/molecules/RollupSetCard/rollupSetModel';

/**
 * Error thrown when attempting to deactivate the last active factor without confirmation.
 * Callers should catch this to show a confirmation dialog, then retry with { confirmLastFactor: true }.
 */
export class LastActiveFactorError extends Error {
  readonly reason = 'last_active_factor' as const;
  constructor(message: string) {
    super(message);
    this.name = 'LastActiveFactorError';
  }
}

/** Factor definition from server (simplified) */
type FactorDefinition = NonNullable<SphereStatusPoint['factorDefinitions']>[number];

/**
 * Derive a factor's lifecycle mode client-side, mirroring the backend's
 * `resolveFactorMode`: mode if present, else derived from `deployed`.
 */
function resolveFactorMode(factor: FactorDefinition): FactorMode {
  if (factor.mode) return factor.mode;
  return factor.deployed === false ? 'paused' : 'active';
}

/**
 * Derive whether a factor should surface on sphere-card drill-down views,
 * mirroring backend `resolveShowOnCards` — only an explicit `false` opts a
 * factor out; `undefined`/`null`/`true` all resolve to shown. Kept in the
 * shared editor hook so every consumer (`factorDefinitionToFormState`, the
 * card-render filter in `getVisibleFactorDefinitions`) uses the same rule.
 */
export function resolveShowOnCards(factor: Pick<FactorDefinition, 'showOnCards'>): boolean {
  return factor.showOnCards !== false;
}

/**
 * Convert server perspective filters to form state format
 * Server: { perspectiveSystemName, operator, assignees }
 * Form: { perspectiveSystemName, includeInHierarchy, assigneeFilter }
 */
function convertServerPerspectiveFiltersToFormState(
  serverFilters: Array<{ perspectiveSystemName: string; operator?: string; assignees?: string[] }> | undefined
): FactorFormState['perspectiveFilters'] {
  if (!serverFilters || serverFilters.length === 0) {
    return [];
  }

  return serverFilters.map((filter) => ({
    perspectiveSystemName: filter.perspectiveSystemName,
    includeInHierarchy: filter.operator !== 'not_in', // not_in means excluded
    assigneeFilter: {
      filterType: (filter.assignees?.length ?? 0) > 0 ? 'specific' : 'any',
      assigneeIds: filter.assignees || [],
      selectedAssignees: [], // Will be populated by UI when editing
    },
  }));
}

/**
 * Convert form state perspective filters to server format
 * Form: { perspectiveSystemName, includeInHierarchy, assigneeFilter }
 * Server: { perspectiveSystemName, operator, assignees }
 */
function convertFormStatePerspectiveFiltersToServer(
  formFilters: FactorFormState['perspectiveFilters']
): Array<{ perspectiveSystemName: string; operator: 'in' | 'not_in'; assignees: string[] }> {
  if (!formFilters || formFilters.length === 0) {
    return [];
  }

  return formFilters
    .filter((filter) => filter.includeInHierarchy) // Only include filters that are part of hierarchy
    .map((filter) => ({
      perspectiveSystemName: filter.perspectiveSystemName,
      operator: 'in' as const, // Included perspectives use 'in'
      assignees: filter.assigneeFilter?.filterType === 'specific'
        ? (filter.assigneeFilter.assigneeIds || [])
        : [],
    }));
}

/**
 * Which pinned sphere ids need a members-route call this apply (EAINT-877 Phase 5). A pin never
 * mints an `exclusions[]` entry on removal — `DELETE .../members/:memberSphereId` decides
 * unlink-vs-exclude server-side (D56) — so this only ever drives the add/remove HTTP calls.
 */
export function diffRollupPins(previousPins: string[], nextPins: string[]): { added: string[]; removed: string[] } {
  return {
    added: nextPins.filter((sphereId) => !previousPins.includes(sphereId)),
    removed: previousPins.filter((sphereId) => !nextPins.includes(sphereId)),
  };
}

/** The minimal `graphClient` shape `syncRollupPins` needs — narrowed so it's mockable without a full `ApiClients`. */
export interface RollupMemberRouteClient {
  post: (url: string, body: unknown) => Promise<unknown>;
  delete: (url: string) => Promise<unknown>;
}

/** The minimal `graphClient` shape `fetchPinnedSphereIds` needs — narrowed the same way. */
export interface RollupRosterRouteClient {
  get: <T = unknown>(url: string) => Promise<T>;
}

interface FactorRosterMember {
  sphereId: string;
  sources: Array<{ kind: string }>;
}

/**
 * A saved `related_spheres` factor's pinned sphere ids, read from the roster route rather than
 * `populationSources` — a `{ kind: 'direct' }` source carries no member ids (D50), so a roster
 * member whose `sources[]` includes `kind: 'direct'` is the only way to recover which spheres are
 * pinned (EAINT-877 Phase 7). There is no dedicated members GET; this reuses the same roster the
 * card's READS AS band reads. Skipped for a factor that hasn't been saved with `populationSources`
 * at all — nothing to resolve pins against.
 */
export async function fetchPinnedSphereIds(
  graphClient: RollupRosterRouteClient,
  sphereId: string,
  factor: Pick<FactorDefinition, 'id' | 'factorType' | 'populationSources'>
): Promise<string[]> {
  if (factor.factorType !== 'related_spheres' || !factor.populationSources?.length) {
    return [];
  }
  const roster = await graphClient.get<{ members?: FactorRosterMember[] }>(
    `/spheres/${sphereId}/factors/${factor.id}/roster`
  );
  return (roster?.members ?? [])
    .filter((member) => member.sources?.some((source) => source.kind === 'direct'))
    .map((member) => member.sphereId);
}

/**
 * Pins ride the members route, one call per pin (D50) — a `{ kind: 'direct' }` sentinel in
 * `populationSources` carries no member id, so this is the only place an added/removed pin
 * actually reaches the server. Called from `applyFactor` for every `related_spheres` factor;
 * inert (zero calls) when `previousPins`/`nextPins` are equal.
 */
export async function syncRollupPins(
  graphClient: RollupMemberRouteClient,
  sphereId: string,
  factorId: string,
  previousPins: string[],
  nextPins: string[]
): Promise<void> {
  const { added, removed } = diffRollupPins(previousPins, nextPins);
  // Each member record is independent (D50: `{ kind: 'direct' }` carries no payload; every pin
  // is its own POST). Fire adds in parallel, then removes in parallel — the two groups stay
  // sequential so a rapid pin-then-unpin of the same sphere still resolves to "unpinned".
  // `{}` not `null` for the POST body — axios omits Content-Type on a null body and the graph
  // service's `POST members/:id` route 415s without `application/json`.
  await Promise.all(
    added.map((memberSphereId) =>
      graphClient.post(`/spheres/${sphereId}/factors/${factorId}/members/${memberSphereId}`, {})
    )
  );
  await Promise.all(
    removed.map((memberSphereId) =>
      graphClient.delete(`/spheres/${sphereId}/factors/${factorId}/members/${memberSphereId}`)
    )
  );
}

/**
 * Derive the UI's `checkInTrigger` value from a factor's server shape.
 * Prefer the explicit server-side cadence (EAINT-742); fall back to legacy
 * inference from schedule shape so factors written before cadence existed
 * still open with the right toggle.
 */
function resolveCheckInTrigger(factor: FactorDefinition): FactorFormState['checkInTrigger'] {
  if (factor.cadence === 'adhoc') return 'adhoc';
  if (factor.cadence === 'scheduled') return 'recurring';
  const legacyTrigger = factor.metadata?.checkInSchedule?.triggerType;
  if (legacyTrigger) return legacyTrigger;
  return factor.checkInSchedule ? 'recurring' : 'adhoc';
}

/**
 * EAINT-875: derive the rule source the form drives from either the modern
 * `populationSources[]` shape or the legacy `monitoringRules` shape, mirroring
 * `normalizePopulationSources` (graph-service `helpers/factors/resolveRoster.ts`)
 * so the frontend sees the exact same source resolveRoster would evaluate.
 *
 * Preferences: source[0] of `populationSources` when present; otherwise
 * normalise `monitoringRules`. Returns null when neither is authored (a
 * brand-new factor with no configured population yet).
 */
function readRuleSource(factor: FactorDefinition): {
  direction: 'inbound' | 'outbound';
  perspectiveSystemName: string;
  filters?: import('types/filters').UnifiedFilters;
} | null {
  const first = factor.populationSources?.[0];
  if (first?.kind === 'rule') {
    return {
      direction: first.direction,
      perspectiveSystemName: first.perspectiveSystemName,
      filters: first.filters,
    };
  }
  // No modern source[0] (or it's a measure/direct kind the form doesn't yet
  // edit) — fall back to legacy monitoringRules. The perspective is either
  // at the top level (canonical) or nested in filters.perspectiveFilters[0]
  // (legacy), mirroring normalizeMonitoringRules; default matches
  // normalizePopulationSources.
  const legacy = factor.monitoringRules; // NOSONAR: intentional EAINT-875 legacy read path (AC #2)
  if (!legacy) return null;
  const perspectiveSystemName =
    legacy.perspectiveSystemName ??
    legacy.filters?.perspectiveFilters?.[0]?.perspectiveSystemName ??
    'center_parent';
  return {
    direction: legacy.direction,
    perspectiveSystemName,
    filters: legacy.filters,
  };
}

/** A stored `childPerspective` of `''` or the legacy string `'null'` means "follows the lens". */
function normaliseChildPerspectiveValue(raw: string | null | undefined): string | null {
  return raw && raw !== 'null' ? raw : null;
}

/**
 * Convert server factor definition to local form state. `pinnedSphereIds` comes from a roster
 * fetch the caller already made (`fetchPinnedSphereIds`) — this function stays synchronous.
 */
function factorDefinitionToFormState(factor: FactorDefinition, pinnedSphereIds: string[] = []): FactorFormState {
  const ruleSource = readRuleSource(factor);
  const filters = ruleSource?.filters;
  // EAINT-875: preserve every population source beyond source[0] verbatim so
  // a legacy multi-source rollup round-trips without silent data loss. The
  // set-card work (EAINT-877) is the surface that will let the user reconcile
  // the tail; until then the form drives source[0] and rides the rest along.
  const preservedPopulationSourcesTail = factor.populationSources && factor.populationSources.length > 1
    ? factor.populationSources.slice(1)
    : undefined;

  const formState: FactorFormState = {
    factorId: factor.id,
    factorType: factor.factorType as FactorFormState['factorType'],
    weight: factor.weight ?? 1,
    name: factor.metadata?.name || '',
    description: factor.metadata?.description || '',
    // Manual factor specific
    commentRequired: factor.metadata?.commentRequired ?? false,
    minCommentLength: factor.metadata?.minCommentLength ?? 0,
    checkInTrigger: resolveCheckInTrigger(factor),
    // EAINT-749: checkInSchedule is a TOP-LEVEL vertex property on the factor,
    // not nested under metadata. addStatusFactor writes it via
    // `.property('checkInSchedule', ...)`; the API returns it top-level in
    // factorDefinitions. Reading from metadata.checkInSchedule silently missed
    // every persisted value — including reminder/escalation thresholds.
    checkInSchedule: factor.checkInSchedule || factor.metadata?.checkInSchedule || {},
    selectedScheduleTemplate: factor.metadata?.selectedScheduleTemplate || null,
    // EAINT-748: pre-first-check-in status. Read authoritative value from
    // metadata.schemaConfig.initialStatus (where addStatusFactor persists it).
    initialStatus:
      (factor.metadata?.schemaConfig?.initialStatus as FactorFormState['initialStatus']) ||
      (factor.metadata?.initialStatus as FactorFormState['initialStatus']) ||
      'unknown',
    mode: resolveFactorMode(factor),
    // EAINT-750: shared advanced-section toggle. Absence resolves to `true`.
    showOnCards: resolveShowOnCards(factor),
    // Arc health specific
    arcSelection: (factor.metadata?.arcSelection as FactorFormState['arcSelection']) ?? { type: 'primary' },
    arcAggregationMethod: (factor.metadata?.arcAggregationMethod as FactorFormState['arcAggregationMethod']) ?? 'worst',
    // Related spheres (Rollup) specific — EAINT-875 reads from modern
    // populationSources when present, falls back to legacy monitoringRules.
    aggregationMethod: (factor.aggregationMethod || 'worst') as FactorFormState['aggregationMethod'],
    weighting: fromStored(factor).weighting,
    emptyBehavior: normaliseEmptyBehavior(factor.metadata?.emptyBehavior ?? factor.metadata?.schemaConfig?.emptyBehavior),
    // EAINT-875: reads via readRuleSource() so a modern populationSources[0] wins, falling
    // back to monitoringRules on legacy factors — same source resolveRoster evaluates.
    perspectiveDirection: ruleSource?.direction || 'inbound',
    filterMode: filters?.filterGroups?.length ? 'advanced' : 'simple',
    perspectiveFilters: convertServerPerspectiveFiltersToFormState(filters?.perspectiveFilters),
    propertyFilters: filters?.propertyFilters || [],
    // Canonical (`expressions`) → editor (`filters`) rename via unifiedGroupToSimple — see
    // contextFlowSerialization for the "same tree, different leaf key" rationale.
    filterGroups: (filters?.filterGroups || []).map(unifiedGroupToSimple),
    // EAINT-877 Phase 7: decompiles populationSources[] + exclusions[] back into
    // scope/direction/tree/pins — falls back to the legacy monitoringRules shape when
    // populationSources is absent (a pre-EAINT-875 factor) so it still opens with its tree intact.
    rollupSet: decompileSet(factor.populationSources, pinnedSphereIds, factor.exclusions, factor.monitoringRules), // NOSONAR: intentional EAINT-875 legacy read path (AC #2)
    // EAINT-877 Phase 8 (D60): always true for related_spheres — see the field's doc comment
    // in statusFactors.ts for why a legacy (populationSources-absent) factor must route through
    // the new compile once the card is the only editor.
    usesPopulationSources: factor.factorType === 'related_spheres',
    // EAINT-875: engine-owned fields the form does not edit yet — preserved verbatim so a save
    // re-emits them intact. Superseded on the related_spheres path by rollupSet+usesPopulationSources
    // above, but retained for the roster (EAINT-938) / exclusions (EAINT-879) surfaces that will
    // consume them as first-class form fields.
    preservedPopulationSourcesTail,
    preservedMeasureSystemName: factor.measureSystemName,
    influenceFloor: typeof factor.influenceFloor === 'number' ? factor.influenceFloor : null,
    coverageFloor:
      typeof factor.minCoverage === 'number'
        ? {
          percent: Math.round(factor.minCoverage * 100),
          status: factor.belowCoverageStatus ?? 'unknown',
          exact: factor.minCoverage,
        }
        : null,
    childPerspective: normaliseChildPerspectiveValue(factor.childPerspective),
  };

  return formState;
}

/** The six canonical health values a factor's currentStatus may render as. */
const CANONICAL_STATUS_SET = new Set<StatusValue>([
  'on_track',
  'at_risk',
  'needs_attention',
  'critical',
  'blocked',
  'unknown',
]);

/**
 * Determine factor status from server data.
 * Lifecycle states (deactivated/paused/observing) take priority.
 * Health branch passes factor.currentStatus through directly — no collapse.
 * Non-canonical server values fall back to 'unknown' rather than rendering
 * an untranslated label / neutral dot for a value outside the vocabulary.
 */
export function getFactorStatus(factor: FactorDefinition): FactorStatus {
  if (factor.active === false) {
    return 'deactivated';
  }
  const mode = resolveFactorMode(factor);
  if (mode === 'paused') {
    return 'paused';
  }
  if (mode === 'observing') {
    return 'observing';
  }
  return factor.currentStatus && CANONICAL_STATUS_SET.has(factor.currentStatus as StatusValue)
    ? (factor.currentStatus as FactorStatus)
    : 'unknown';
}

/**
 * Validate a factor form state
 */
function validateFactor(formState: FactorFormState): FactorValidationResult {
  const errors: Record<string, string> = {};

  // Factor type is required
  if (!formState.factorType) {
    errors.factorType = 'Factor type is required';
  }

  // Weight must be between 0 and 1
  if (formState.weight < 0 || formState.weight > 1) {
    errors.weight = 'Weight must be between 0 and 1';
  }

  // Manual factor specific validation
  if (formState.factorType === 'manual') {
    if (formState.commentRequired && formState.minCommentLength < 0) {
      errors.minCommentLength = 'Minimum comment length cannot be negative';
    }

    // Recurring schedule requires RRULE
    if (formState.checkInTrigger === 'recurring') {
      if (!formState.checkInSchedule.rrule && formState.selectedScheduleTemplate !== 'custom') {
        // Template selected but no rrule - this is OK, template will provide it
      } else if (formState.selectedScheduleTemplate === 'custom' && !formState.checkInSchedule.rrule) {
        errors.rrule = 'Custom schedule requires a recurrence rule';
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Check if two form states are equal (for dirty detection)
 */
function areFormStatesEqual(a: FactorFormState | null, b: FactorFormState | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;

  // Compare key fields — the rollup preserved-fields (EAINT-875) participate
  // so a save that would re-emit a different payload flips the dirty marker.
  // Required by EAINT-877 AC: the amber "count not applied yet" marker.
  return (
    a.factorType === b.factorType &&
    a.weight === b.weight &&
    a.name === b.name &&
    a.description === b.description &&
    a.commentRequired === b.commentRequired &&
    a.minCommentLength === b.minCommentLength &&
    a.checkInTrigger === b.checkInTrigger &&
    a.initialStatus === b.initialStatus &&
    a.aggregationMethod === b.aggregationMethod &&
    a.emptyBehavior === b.emptyBehavior &&
    a.perspectiveDirection === b.perspectiveDirection &&
    a.weighting === b.weighting &&
    a.influenceFloor === b.influenceFloor &&
    a.coverageFloor?.percent === b.coverageFloor?.percent &&
    a.coverageFloor?.status === b.coverageFloor?.status &&
    a.childPerspective === b.childPerspective &&
    a.preservedMeasureSystemName === b.preservedMeasureSystemName &&
    JSON.stringify(a.checkInSchedule) === JSON.stringify(b.checkInSchedule) &&
    JSON.stringify(a.perspectiveFilters) === JSON.stringify(b.perspectiveFilters) &&
    JSON.stringify(a.propertyFilters) === JSON.stringify(b.propertyFilters) &&
    JSON.stringify(a.filterGroups) === JSON.stringify(b.filterGroups) &&
    JSON.stringify(a.rollupSet) === JSON.stringify(b.rollupSet) &&
    a.usesPopulationSources === b.usesPopulationSources &&
    JSON.stringify(a.preservedPopulationSourcesTail) === JSON.stringify(b.preservedPopulationSourcesTail)
  );
}

export interface UseStatusFactorsStateProps {
  sphere: Sphere;
  apiClients: ApiClients | null;
  hasEditPermission?: boolean;
  onFactorAdded?: () => void;
  onFactorRemoved?: () => void;
  onFactorUpdated?: () => void;
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
}

export interface UseStatusFactorsStateReturn {
  // Data
  factors: LocalFactor[];
  selectedFactorId: string | null;
  selectedFactor: LocalFactor | null;

  // Loading state
  loading: boolean;
  error: string | null;
  isSaving: boolean;

  // Global state
  hasDirtyFactors: boolean;
  dirtyFactorCount: number;
  hasValidationErrors: boolean;

  // Selection
  selectFactor: (id: string | null) => void;

  // Factor operations
  addNewFactor: () => string;
  updateFactor: (id: string, updates: Partial<FactorFormState>) => void;
  applyFactor: (id: string) => Promise<void>;
  deleteFactor: (id: string, options?: { confirmLastFactor?: boolean }) => Promise<void>;
  revertFactor: (id: string) => void;

  // Persistence
  saveAllChanges: () => Promise<void>;
  refreshFromServer: () => Promise<void>;

  // Deactivation
  deactivatedFactors: LocalFactor[];
  reactivateFactor: (id: string) => Promise<void>;

  // Lifecycle mode
  setMode: (id: string, mode: FactorMode) => Promise<void>;

  // EAINT-750: shared header show-on-cards toggle — immediate persist, no Apply.
  setShowOnCards: (id: string, showOnCards: boolean) => Promise<void>;

  // Validation
  validateFactor: (id: string) => FactorValidationResult;
}

/**
 * Hook for managing status factors state with dirty tracking and dual-save logic
 */
export function useStatusFactorsState({
  sphere,
  apiClients,
  hasEditPermission: _hasEditPermission,
  onFactorAdded,
  onFactorRemoved,
  onFactorUpdated,
  onUnsavedChangesChange,
}: UseStatusFactorsStateProps): UseStatusFactorsStateReturn {
  const logAnalyticsEvent = useAnalytics();

  // Core state
  const [factors, setFactors] = useState<LocalFactor[]>([]);
  const [selectedFactorId, setSelectedFactorId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, _setIsSaving] = useState(false);
  const [deactivatedFactors, setDeactivatedFactors] = useState<LocalFactor[]>([]);

  // Ref to access current factors without causing dependency issues
  const factorsRef = useRef<LocalFactor[]>(factors);
  useEffect(() => {
    factorsRef.current = factors;
  }, [factors]);

  // Derived state — search both active and deactivated factors for selection
  const selectedFactor = useMemo(
    () =>
      factors.find((f) => f.id === selectedFactorId) ||
      deactivatedFactors.find((f) => f.id === selectedFactorId) ||
      null,
    [factors, deactivatedFactors, selectedFactorId]
  );

  const hasDirtyFactors = useMemo(
    () => factors.some((f) => f.isDirty || f.isNew),
    [factors]
  );

  const dirtyFactorCount = useMemo(
    () => factors.filter((f) => f.isDirty || f.isNew).length,
    [factors]
  );

  const hasValidationErrors = useMemo(
    () => factors.some((f) => f.hasValidationErrors),
    [factors]
  );

  // Notify parent of unsaved changes
  useEffect(() => {
    onUnsavedChangesChange?.(hasDirtyFactors);
  }, [hasDirtyFactors, onUnsavedChangesChange]);

  // Fetch factors from server
  const refreshFromServer = useCallback(async () => {
    if (!apiClients?.graphClient || !sphere.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClients.graphClient.get<{ sphere: Sphere }>(
        `/spheres/${sphere.id}?getStatus=true&getStatusFactors=true&getPerspectives=false&getAssignedPerspectives=false&skipCache=true`
      );

      if (response?.sphere?.status) {
        const serverFactors = response.sphere.status.factorDefinitions || [];
        // Use ref to access current factors without causing dependency loop
        const currentFactors = factorsRef.current;

        // Convert to local factors, preserving any local changes
        const updatedFactors: LocalFactor[] = await Promise.all(serverFactors.map(async (serverFactor: FactorDefinition) => {
          const existingLocal = currentFactors.find((f) => f.id === serverFactor.id);
          const pinnedSphereIds = await fetchPinnedSphereIds(apiClients.graphClient, sphere.id, serverFactor);
          const formState = factorDefinitionToFormState(serverFactor, pinnedSphereIds);

          if (existingLocal?.isDirty) {
            // Preserve local changes
            return {
              ...existingLocal,
              originalData: formState,
              status: getFactorStatus(serverFactor),
              pausedAt: serverFactor.pausedAt,
              pausedBy: serverFactor.pausedBy,
              velocityScore: serverFactor.velocityScore,
              velocityBucket: serverFactor.velocityBucket,
              coverage: serverFactor.coverage,
              currentStatus: CANONICAL_STATUS_SET.has(serverFactor.currentStatus as StatusValue)
                ? (serverFactor.currentStatus as StatusValue)
                : undefined,
            };
          }

          return {
            id: serverFactor.id,
            isNew: false,
            isDirty: false,
            hasValidationErrors: false,
            validationErrors: {},
            status: getFactorStatus(serverFactor),
            localData: formState,
            originalData: formState,
            pausedAt: serverFactor.pausedAt,
            pausedBy: serverFactor.pausedBy,
            velocityScore: serverFactor.velocityScore,
            velocityBucket: serverFactor.velocityBucket,
            coverage: serverFactor.coverage,
            currentStatus: CANONICAL_STATUS_SET.has(serverFactor.currentStatus as StatusValue)
              ? (serverFactor.currentStatus as StatusValue)
              : undefined,
          };
        }));

        // Keep any new factors that haven't been saved yet
        const newFactors = currentFactors.filter((f) => f.isNew);

        setFactors([...updatedFactors, ...newFactors]);
      }
    } catch (err: any) {
      console.error('[useStatusFactorsState] Failed to fetch factors:', err);
      setError(err.message || 'Failed to load status factors');
    } finally {
      setLoading(false);
    }
  }, [apiClients, sphere.id]);

  // Initial fetch - only run when sphere.id or apiClients change
  useEffect(() => {
    refreshFromServer();
  }, [refreshFromServer]);

  // Auto-select first factor when factors are loaded and none is selected
  useEffect(() => {
    if (!loading && factors.length > 0 && !selectedFactorId) {
      setSelectedFactorId(factors[0].id);
    }
  }, [loading, factors, selectedFactorId]);

  // Select a factor
  const selectFactor = useCallback((id: string | null) => {
    setSelectedFactorId(id);
  }, []);

  // Add a new factor (creates a draft)
  const addNewFactor = useCallback((): string => {
    const newId = uuidv4();
    const newLocalFactor: LocalFactor = {
      id: newId,
      isNew: true,
      isDirty: true,
      hasValidationErrors: true, // New factors start invalid (no type selected)
      validationErrors: { factorType: 'Factor type is required' },
      status: 'draft',
      localData: { ...DEFAULT_FACTOR_FORM_STATE },
      originalData: null,
    };

    setFactors((prev) => [...prev, newLocalFactor]);
    setSelectedFactorId(newId);

    return newId;
  }, []);

  // Update a factor's local data
  const updateFactor = useCallback((id: string, updates: Partial<FactorFormState>) => {
    setFactors((prev) =>
      prev.map((factor) => {
        if (factor.id !== id) return factor;

        const updatedLocalData = { ...factor.localData, ...updates };
        // EAINT-877 Phase 8 (D60): a draft that just picked related_spheres as its type
        // starts from DEFAULT_FACTOR_FORM_STATE's usesPopulationSources: false — force it
        // true here too, so a brand-new factor's set-card edits compile the same way an
        // existing one's do (see the field's doc comment in statusFactors.ts).
        if (updatedLocalData.factorType === 'related_spheres') {
          updatedLocalData.usesPopulationSources = true;
        }
        const validation = validateFactor(updatedLocalData);
        const isDirty = factor.isNew || !areFormStatesEqual(updatedLocalData, factor.originalData);

        return {
          ...factor,
          localData: updatedLocalData,
          isDirty,
          hasValidationErrors: !validation.isValid,
          validationErrors: validation.errors,
          status: factor.isNew ? 'draft' : factor.status,
        };
      })
    );
  }, []);

  // Build API payload from factor form state
  const buildFactorPayload = useCallback((localData: FactorFormState, storedData: FactorFormState | null = null) => {
    const payload: Record<string, unknown> = {
      factorType: localData.factorType,
      weight: localData.weight,
    };

    if (localData.mode) {
      payload.mode = localData.mode;
    }

    // EAINT-750: send showOnCards on every write so the vertex reflects the
    // owner's intent for both new factors (defaults to true) and toggles on
    // existing ones (an explicit `true` overwrites any prior `false`). The
    // backend persists both values; only an explicit `false` opts out at
    // read time.
    payload.showOnCards = localData.showOnCards;

    if (localData.factorType === 'manual') {
      // Cadence is the explicit, server-enforced discriminator (EAINT-742).
      // 'adhoc' → no schedule of any kind; 'scheduled' → schedule required.
      // The UI's local `checkInTrigger` carries other scheduling flavors
      // (orbit/timeframe/recurring); anything non-adhoc maps to 'scheduled'.
      const cadence: 'adhoc' | 'scheduled' =
        localData.checkInTrigger === 'adhoc' ? 'adhoc' : 'scheduled';
      payload.cadence = cadence;
      if (cadence === 'scheduled') {
        // Top-level `checkInSchedule` is what the create/update route reads;
        // metadata.checkInSchedule is kept alongside so downstream readers that
        // still infer from the metadata shape (legacy) keep working.
        payload.checkInSchedule = localData.checkInSchedule;
      }
      payload.metadata = {
        commentRequired: localData.commentRequired,
        minCommentLength: localData.minCommentLength,
        ...(cadence === 'scheduled' ? { checkInSchedule: localData.checkInSchedule } : {}),
        // EAINT-745 follow-up: persist which template the owner picked so the
        // editor remembers the selection on reopen. Read at
        // factorDefinitionToFormState L144 (`factor.metadata?.selectedScheduleTemplate`);
        // without writing it here, the read always resolved to null and the
        // template gallery re-appeared after refresh. Send `null` explicitly
        // so switching from 'weekly_1_1' back to 'custom' or clearing the
        // selection round-trips instead of leaving stale metadata behind.
        ...(cadence === 'scheduled'
          ? { selectedScheduleTemplate: localData.selectedScheduleTemplate ?? null }
          : {}),
      };
      // EAINT-748: send initialStatus at the top level of the payload so the
      // route can validate it against its enum schema. The route also mirrors
      // it into metadata.schemaConfig on the server for recalc reads.
      payload.initialStatus = localData.initialStatus;
    }

    if (localData.factorType === 'related_spheres') {
      const combine = toStored(fromStored(localData));
      payload.aggregationMethod = combine.aggregationMethod;
      payload.weighting = combine.weighting;
      payload.metadata = {
        emptyBehavior: localData.emptyBehavior,
      };
      if (localData.usesPopulationSources) {
        // EAINT-877 Phase 5: the Rollup Set Card compiles its own authoring model down —
        // `populationSources` wins over `monitoringRules` at read time (resolveRoster.ts's
        // `normalizePopulationSources`), so a factor in this mode sends only the compiled
        // sources, never the legacy fields below. The `preservedPopulationSourcesTail` (EAINT-875)
        // rides along so a multi-source rollup whose source[1..n] the card cannot yet author
        // (measure/direct kinds) still round-trips without silent data loss.
        payload.populationSources = [
          ...compileSet(localData.rollupSet),
          ...(localData.preservedPopulationSourcesTail ?? []),
        ];
      } else {
        // EAINT-875: legacy fallback — write the modern populationSources shape from the flat
        // form fields (behaviour-identical to the legacy monitoringRules the write used to emit).
        // Post-EAINT-877 Phase 8 this branch is dead for `related_spheres` (usesPopulationSources
        // is always true), retained for defensive fallback / other factor-type variants.
        const serverPerspectiveFilters = convertFormStatePerspectiveFiltersToServer(localData.perspectiveFilters);
        const filters = {
          perspectiveFilters:
            serverPerspectiveFilters.length > 0 ? serverPerspectiveFilters : undefined,
          propertyFilters:
            localData.propertyFilters.length > 0 ? localData.propertyFilters : undefined,
          // Editor (`filters`) → canonical (`expressions`) rename via simpleGroupToUnified.
          filterGroups:
            localData.filterGroups && localData.filterGroups.length > 0
              ? localData.filterGroups.map(simpleGroupToUnified) : undefined,
        };
        const perspectiveSystemName =
          serverPerspectiveFilters[0]?.perspectiveSystemName ?? 'center_parent';
        const ruleSource: PopulationSource = {
          kind: 'rule',
          direction: localData.perspectiveDirection,
          perspectiveSystemName,
          filters,
        };
        // Preserve any legacy multi-source rollup entries beyond source[0]. The form drives
        // source[0]; the tail rides along until EAINT-877's set card owns the multi-source
        // reconciliation UX end-to-end.
        payload.populationSources = [ruleSource, ...(localData.preservedPopulationSourcesTail ?? [])];
      }
      // Engine-owned fields the form does not edit ride along on every save (both compile-down
      // modes). `exclusions[]` is deliberately absent: the roster exclude/restore routes own it, and
      // re-sending a stale loaded copy would overwrite their writes.
      // A guardrail switched off sends `null` (the server drops the property) only when one was
      // stored; a factor that never had one carries no key.
      if (localData.influenceFloor !== null) {
        payload.influenceFloor = localData.influenceFloor;
      } else if (storedData?.influenceFloor != null) {
        payload.influenceFloor = null;
      }
      if (localData.coverageFloor !== null) {
        payload.minCoverage = localData.coverageFloor.exact ?? localData.coverageFloor.percent / 100;
        payload.belowCoverageStatus = localData.coverageFloor.status;
      } else if (storedData?.coverageFloor != null) {
        payload.minCoverage = null;
      }
      payload.childPerspective = localData.childPerspective;
      if (localData.preservedMeasureSystemName) {
        payload.measureSystemName = localData.preservedMeasureSystemName;
      }
    }

    // `name` and `description` are read back from `factor.metadata` (see
    // factorDefinitionToFormState). The route schema has no top-level name
    // field, so sending them as top-level payload keys was a silent drop.
    // Merge them into metadata after the type-specific block so no branch
    // clobbers them.
    if (localData.name || localData.description) {
      const existingMetadata = (payload.metadata as Record<string, unknown> | undefined) ?? {};
      payload.metadata = {
        ...existingMetadata,
        ...(localData.name ? { name: localData.name } : {}),
        ...(localData.description ? { description: localData.description } : {}),
      };
    }

    return payload;
  }, []);

  // Apply factor: validate, persist to server, and refresh
  // Fires once per save that touched a combine, guardrail or edge-case field (always for a new factor).
  const logRollupCombinationSaved = useCallback(
    (factor: LocalFactor) => {
      const next = factor.localData;
      const before = factor.originalData;
      const changed =
        !before ||
        next.aggregationMethod !== before.aggregationMethod ||
        next.weighting !== before.weighting ||
        next.influenceFloor !== before.influenceFloor ||
        next.coverageFloor?.percent !== before.coverageFloor?.percent ||
        next.coverageFloor?.status !== before.coverageFloor?.status ||
        next.emptyBehavior !== before.emptyBehavior ||
        next.childPerspective !== before.childPerspective;
      if (!changed) return;
      const axes = fromStored(next);
      logAnalyticsEvent('rollup_combination_saved', undefined, {
        factorId: factor.id,
        method: axes.wins,
        weighting: axes.weighting,
        weightFloorSet: next.influenceFloor !== null,
        coverageFloorSet: next.coverageFloor !== null,
        emptyStatus: next.emptyBehavior,
        readThrough: next.childPerspective ? 'pinned' : 'follows',
      });
    },
    [logAnalyticsEvent]
  );

  const applyFactor = useCallback(async (id: string) => {
    const factor = factors.find((f) => f.id === id);
    if (!factor) return;
    if (!apiClients?.graphClient) return;

    const validation = validateFactor(factor.localData);
    if (!validation.isValid) {
      setFactors((prev) =>
        prev.map((f) =>
          f.id === id
            ? { ...f, hasValidationErrors: true, validationErrors: validation.errors }
            : f
        )
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = buildFactorPayload(factor.localData, factor.originalData);

      if (factor.isNew) {
        // EAINT-756: previously we filtered the local draft and then relied on
        // refreshFromServer to re-add the server row. That created a race —
        // the frontend's local uuid never matched the server's generated id,
        // so the reconciliation loop couldn't tie them together and the
        // factor visually disappeared from the rail. Fix: adopt the server's
        // returned factor row in-place, keeping the local `id` slot occupied
        // by the same object that now holds the server id and clean flags.
        const response = await apiClients.graphClient.post(
          `/sphere/${sphere.id}/points/status/factors`,
          payload
        );
        const serverFactor = (response as any)?.data?.factor
          ?? (response as any)?.factor
          ?? null;

        if (serverFactor?.id) {
          const formState = factorDefinitionToFormState(serverFactor);
          const adoptedFactor: LocalFactor = {
            id: serverFactor.id,
            isNew: false,
            isDirty: false,
            hasValidationErrors: false,
            validationErrors: {},
            status: getFactorStatus(serverFactor),
            localData: formState,
            originalData: formState,
            pausedAt: serverFactor.pausedAt,
            pausedBy: serverFactor.pausedBy,
            velocityScore: serverFactor.velocityScore,
            velocityBucket: serverFactor.velocityBucket,
            coverage: serverFactor.coverage,
            currentStatus: CANONICAL_STATUS_SET.has(serverFactor.currentStatus as StatusValue)
              ? (serverFactor.currentStatus as StatusValue)
              : undefined,
          };
          // Update both state and the sync ref so the immediately-following
          // refreshFromServer sees the adopted row (and doesn't preserve the
          // now-superseded local draft as an `isNew` orphan on line 398).
          const nextFactors = factorsRef.current.map((f) =>
            f.id === id ? adoptedFactor : f
          );
          factorsRef.current = nextFactors;
          setFactors(nextFactors);

          if (factor.localData.factorType === 'related_spheres') {
            await syncRollupPins(apiClients.graphClient, sphere.id, serverFactor.id, [], factor.localData.rollupSet.pins);
          }
        } else {
          // Server response was missing the factor object (older API shape or
          // an unexpected format). Fall back to the old filter+refresh path;
          // the reconciliation loop will still recover on the refresh.
          const nextFactors = factorsRef.current.filter((f) => f.id !== id);
          factorsRef.current = nextFactors;
          setFactors(nextFactors);
        }
        onFactorAdded?.();
      } else {
        await apiClients.graphClient.patch(
          `/sphere/${sphere.id}/points/status/factors/${factor.id}`,
          payload
        );

        if (factor.localData.factorType === 'related_spheres') {
          await syncRollupPins(
            apiClients.graphClient,
            sphere.id,
            factor.id,
            factor.originalData?.rollupSet.pins ?? [],
            factor.localData.rollupSet.pins
          );
        }

        // Adopt localData as the new baseline BEFORE refreshFromServer runs. Otherwise a slight
        // asymmetry between compileSet(localData.rollupSet) and decompileSet(server response)
        // makes `areFormStatesEqual(localData, refreshedOriginalData)` return false and
        // `hasDirtyFactors` stays true — the "discard changes" warning persists after a
        // successful Apply. The refresh can still update currentStatus / coverage / etc.
        setFactors((prev) =>
          prev.map((f) =>
            f.id === factor.id
              ? { ...f, isDirty: false, originalData: f.localData }
              : f
          )
        );

        onFactorUpdated?.();
      }

      if (factor.localData.factorType === 'related_spheres') {
        logRollupCombinationSaved(factor);
      }

      await refreshFromServer();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save factor';
      console.error('[useStatusFactorsState] Failed to apply factor:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [factors, apiClients, sphere.id, buildFactorPayload, onFactorAdded, onFactorUpdated, refreshFromServer, logRollupCombinationSaved]);

  // Delete/deactivate a factor
  const deleteFactor = useCallback(async (id: string, options?: { confirmLastFactor?: boolean }) => {
    const factor = factors.find((f) => f.id === id);
    if (!factor) return;

    // If it's a new factor, just remove from local state
    if (factor.isNew) {
      setFactors((prev) => prev.filter((f) => f.id !== id));
      if (selectedFactorId === id) {
        setSelectedFactorId(null);
      }
      return;
    }

    // Otherwise, deactivate/delete from server
    if (!apiClients?.graphClient) return;

    try {
      const url = `/sphere/${sphere.id}/points/status/factors/${id}${
        options?.confirmLastFactor ? '?confirmLastFactor=true' : ''
      }`;
      const result = await apiClients.graphClient.delete(url);

      if (result?.action === 'deactivated') {
        // Preserve factor in deactivated list with deactivation metadata
        setDeactivatedFactors((prev) => [...prev, {
          ...factor,
          isDirty: false,
          status: 'deactivated' as const,
          deactivatedAt: result.deactivatedAt || new Date().toISOString(),
          deactivatedBy: result.deactivatedBy,
        }]);
      }

      // Remove from active factors list
      setFactors((prev) => prev.filter((f) => f.id !== id));
      if (selectedFactorId === id) {
        setSelectedFactorId(null);
      }
      onFactorRemoved?.();
    } catch (err: unknown) {
      // Surface 409 (last active factor) as a specific error for confirmation dialog
      if (
        err != null &&
        typeof err === 'object' &&
        'response' in err
      ) {
        const response = (err as { response: { status?: number; data?: { reason?: string; details?: string } } }).response;
        if (response?.status === 409 && response?.data?.reason === 'last_active_factor') {
          throw new LastActiveFactorError(
            response.data.details || 'This is the last active status factor'
          );
        }
      }
      const message = err instanceof Error ? err.message : 'Failed to deactivate factor';
      setError(message);
      throw err;
    }
  }, [factors, apiClients, sphere.id, selectedFactorId, onFactorRemoved]);

  // Reactivate a previously deactivated factor
  const reactivateFactor = useCallback(async (id: string) => {
    if (!apiClients?.graphClient) return;

    try {
      await apiClients.graphClient.patch(
        `/sphere/${sphere.id}/points/status/factors/${id}`,
        { active: true, deployed: true }
      );

      // Remove from deactivated list
      setDeactivatedFactors((prev) => prev.filter((f) => f.id !== id));

      // Refresh active factors from server to get updated data
      await refreshFromServer();
      onFactorUpdated?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reactivate factor';
      setError(message);
      throw err;
    }
  }, [apiClients, sphere.id, refreshFromServer, onFactorUpdated]);

  // Set a factor's lifecycle mode (active/observing/paused) — shared by pause/resume/observe-toggle
  const setMode = useCallback(async (id: string, mode: FactorMode) => {
    if (!apiClients?.graphClient) return;

    const factor = factors.find((f) => f.id === id);
    const fromMode = factor?.localData.mode ?? 'active';

    try {
      await apiClients.graphClient.patch(
        `/sphere/${sphere.id}/points/status/factors/${id}`,
        { mode }
      );

      // 'paused' and 'observing' surface as the row's status; 'active' leaves it alone.
      const modeStatus = mode === 'paused' || mode === 'observing' ? mode : undefined;

      setFactors((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                status: modeStatus ?? f.status,
                localData: { ...f.localData, mode },
                originalData: f.originalData ? { ...f.originalData, mode } : f.originalData,
              }
            : f
        )
      );

      await refreshFromServer();
      onFactorUpdated?.();

      logAnalyticsEvent('status_factor_mode_changed', undefined, {
        sphereId: sphere.id,
        factorType: factor?.localData.factorType,
        fromMode,
        toMode: mode,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update factor mode';
      setError(message);
      throw err;
    }
  }, [apiClients, sphere.id, factors, refreshFromServer, onFactorUpdated, logAnalyticsEvent]);

  // EAINT-750: immediate PATCH for the shared header show-on-cards toggle.
  // Mirrors setMode's pattern (the neighbor control in the header) so both
  // header affordances persist on click rather than waiting for Apply — the
  // Apply button is Configure-only, and the switch lives in the shared header
  // where Configure isn't required.
  const setShowOnCards = useCallback(async (id: string, showOnCards: boolean) => {
    if (!apiClients?.graphClient) return;

    const factor = factors.find((f) => f.id === id);
    const fromShowOnCards = factor?.localData.showOnCards ?? true;

    try {
      await apiClients.graphClient.patch(
        `/sphere/${sphere.id}/points/status/factors/${id}`,
        { showOnCards }
      );

      setFactors((prev) =>
        prev.map((f) =>
          f.id === id
            ? {
                ...f,
                localData: { ...f.localData, showOnCards },
                originalData: f.originalData
                  ? { ...f.originalData, showOnCards }
                  : f.originalData,
              }
            : f
        )
      );

      await refreshFromServer();
      onFactorUpdated?.();

      logAnalyticsEvent('status_factor_show_on_cards_changed', undefined, {
        sphereId: sphere.id,
        factorType: factor?.localData.factorType,
        fromShowOnCards,
        toShowOnCards: showOnCards,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update card visibility';
      setError(message);
      throw err;
    }
  }, [apiClients, sphere.id, factors, refreshFromServer, onFactorUpdated, logAnalyticsEvent]);

  // Revert a factor to its original state
  const revertFactor = useCallback((id: string) => {
    setFactors((prev) =>
      prev.map((factor) => {
        if (factor.id !== id) return factor;

        // If it's a new factor, remove it
        if (factor.isNew) {
          return factor; // Will be filtered out separately
        }

        // Revert to original
        if (factor.originalData) {
          return {
            ...factor,
            localData: { ...factor.originalData },
            isDirty: false,
            hasValidationErrors: false,
            validationErrors: {},
          };
        }

        return factor;
      }).filter((factor) => !(factor.id === id && factor.isNew))
    );

    // If we removed the selected factor, clear selection
    const factor = factors.find((f) => f.id === id);
    if (factor?.isNew && selectedFactorId === id) {
      setSelectedFactorId(null);
    }
  }, [factors, selectedFactorId]);

  // Save all dirty factors to server
  const saveAllChanges = useCallback(async () => {
    if (!apiClients?.graphClient) return;

    const dirtyFactors = factors.filter((f) => f.isDirty || f.isNew);
    if (dirtyFactors.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      for (const factor of dirtyFactors) {
        const payload = buildFactorPayload(factor.localData, factor.originalData);

        if (factor.isNew) {
          await apiClients.graphClient.post(
            `/sphere/${sphere.id}/points/status/factors`,
            payload
          );
          onFactorAdded?.();
        } else {
          await apiClients.graphClient.patch(
            `/sphere/${sphere.id}/points/status/factors/${factor.id}`,
            payload
          );
          onFactorUpdated?.();
        }
      }

      await refreshFromServer();
    } catch (err: any) {
      console.error('[useStatusFactorsState] Failed to save factors:', err);
      setError(err.message || 'Failed to save changes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [factors, apiClients, sphere.id, buildFactorPayload, onFactorAdded, onFactorUpdated, refreshFromServer]);

  // Validate a specific factor
  const validateFactorById = useCallback(
    (id: string): FactorValidationResult => {
      const factor = factors.find((f) => f.id === id);
      if (!factor) {
        return { isValid: false, errors: { factor: 'Factor not found' } };
      }
      return validateFactor(factor.localData);
    },
    [factors]
  );

  return {
    // Data
    factors,
    selectedFactorId,
    selectedFactor,

    // Loading state
    loading,
    error,
    isSaving,

    // Global state
    hasDirtyFactors,
    dirtyFactorCount,
    hasValidationErrors,

    // Selection
    selectFactor,

    // Factor operations
    addNewFactor,
    updateFactor,
    applyFactor,
    deleteFactor,
    revertFactor,

    // Persistence
    saveAllChanges,
    refreshFromServer,

    // Deactivation
    deactivatedFactors,
    reactivateFactor,

    // Lifecycle mode
    setMode,

    // EAINT-750
    setShowOnCards,

    // Validation
    validateFactor: validateFactorById,
  };
}
